/**
 * Tasks and All activity — the words both sides of the two list areas share
 * (`docs/activity.md` §8).
 */
import { clock } from '../requests/words.js';
/**
 * Where a task's due date falls, as keys that sort in the order the groups
 * are drawn (§8.0): the engine orders groups by value, so the key carries the
 * order and the label is read from here. *This week* ends **Saturday** —
 * Sunday first (do-not-substitute 20). No date is `null`, which the engine
 * sorts last.
 */
export const DUE_GROUPS = ['1-late', '2-today', '3-week', '4-later'];
export const DUE_GROUP_LABELS = {
    '1-late': 'Late',
    '2-today': 'Today',
    '3-week': 'This week',
    '4-later': 'Later',
};
/** A task with no due date, in the group row. */
export const NO_DUE_LABEL = 'No date';
/** The group row's words for a key the engine hands back (`''` is no date). */
export function dueGroupLabel(key) {
    if (!key)
        return NO_DUE_LABEL;
    return DUE_GROUP_LABELS[key] ?? key;
}
/**
 * A bucket's group key on a task list's *By bucket* view (§8.6): the bucket's
 * place, two digits, then its name — so the engine's value order is the
 * bucket order — and the label is the part after the dash.
 */
export function bucketKeyLabel(key) {
    if (!key)
        return 'No bucket';
    const dash = key.indexOf('-');
    return dash >= 0 ? key.slice(dash + 1) : key;
}
/** All activity's kinds (§3, §8.3), as the `kind` column stores them. */
export const FEED_KINDS = [
    'sms_in', 'sms_out', 'email_in', 'email_out', 'call', 'note', 'voice', 'task', 'appointment', 'meeting',
];
export const FEED_KIND_LABELS = {
    sms_in: 'Text in',
    sms_out: 'Text out',
    email_in: 'Email in',
    email_out: 'Email out',
    call: 'Call',
    note: 'Note',
    voice: 'Voice note',
    task: 'Task',
    appointment: 'Site visit',
    meeting: 'Meeting',
};
/** The message kinds — *Customer messages* is these, not automatic (§8.3). */
export const FEED_MESSAGE_KINDS = ['sms_in', 'sms_out', 'email_in', 'email_out'];
/** The saved views Activity makes for a person the first time a tab opens (§8.1, §8.3, §8.6). */
export const TASK_VIEW_NAMES = {
    myOpen: 'My open',
    late: 'Late',
    dueThisWeek: 'Due this week',
    gaveToOthers: 'I gave to others',
    /* Board 9 drew *Done*; Aaron (Oct 23): finished tasks leave the list and come back under *Completed*. */
    completed: 'Completed',
    everyonesOpen: 'Everyone’s open',
    teamLate: 'Team late',
    byBucket: 'By bucket',
};
export const FEED_VIEW_NAMES = {
    today: 'Today',
    thisWeek: 'This week',
    customerMessages: 'Customer messages',
    callsAndNotes: 'Calls & notes',
    notAnswered: 'Not answered',
};
/** *Done by Maya Patel* — and, for several people, *done for Maya and Luis* (*for all n* past three). */
export function doneForWords(firstNames) {
    if (firstNames.length < 2)
        return null;
    if (firstNames.length > 3)
        return `done for all ${firstNames.length}`;
    if (firstNames.length === 2)
        return `done for ${firstNames[0]} and ${firstNames[1]}`;
    return `done for ${firstNames.slice(0, -1).join(', ')} and ${firstNames[firstNames.length - 1]}`;
}
/** *+3 on last Thursday*, *−2 on last Thursday*, *same as last Thursday* (§8.3). */
export function versusLastWeekWords(today, lastWeek, weekday) {
    const change = today - lastWeek;
    if (change === 0)
        return `same as last ${weekday}`;
    return `${change > 0 ? '+' : '−'}${Math.abs(change)} on last ${weekday}`;
}
// ---------------------------------------------------------------------------
// The Task sheet (Aaron, Oct 25)
// ---------------------------------------------------------------------------
/**
 * A History line for a tick (note 1.2: the log said the person *ticked it*,
 * where it should say they *marked the task complete*): *Maya marked the
 * task complete — done for both*, *— done for all 3*; taking the tick back
 * is *Luis reopened the task*, the word the done band's own **Reopen** says.
 */
export function taskTickWords(who, done, people) {
    if (!done)
        return `${who} reopened the task`;
    const forWhom = people === 2 ? ' — done for both' : people > 2 ? ` — done for all ${people}` : '';
    return `${who} marked the task complete${forWhom}`;
}
/**
 * The grey line under a timed task's fields (§8.2): *Has a time, so it shows
 * on Tom’s calendar at 2:00 PM (20 min).* — *on their calendars* for
 * several. The server says it for a saved task and the sheet for one being
 * changed, so it is written once.
 */
export function taskCalendarLine(firstNames, minutes, durationMinutes) {
    if (firstNames.length === 0)
        return null;
    const where = firstNames.length === 1 ? `on ${firstNames[0]}’s calendar` : 'on their calendars';
    return `Has a time, so it shows ${where} at ${clock(minutes)} (${durationMinutes ?? 20} min).`;
}
//# sourceMappingURL=tasks.js.map